/**
 * Chart of Accounts add / edit / bulkadd request bodies and V2 error mapping.
 * sendResErrV2 returns HTTP 4xx/5xx with { status, message, data, code }.
 * `data` is either novadbmongo `{ index0: { field: [messages] } }` or Joi `[{ field, message }]`.
 */

const WRITE_KEYS = [
  'glcode',
  'name',
  'type',
  'category',
  'subcategory',
  'checklist_id',
  'checklist_id_detail',
  'location',
  'locationId',
];

const BULK_KEYS = [
  'glcode',
  'name',
  'type',
  'category',
  'subcategory',
  'checklistref',
  'location',
];

const INDEX_KEY = /^index(\d+)$/;

function messageText(value) {
  if (value == null || value === false) return '';
  if (Array.isArray(value)) {
    return value.map(messageText).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    return typeof value.message === 'string' ? value.message : '';
  }
  const text = String(value).trim();
  return text;
}

function assignField(target, field, value) {
  const text = messageText(value);
  if (!field || !text || target[field]) return;
  target[field] = text;
}

function assignRow(rowErrors, rowIndex, field, value) {
  if (!Number.isInteger(rowIndex) || rowIndex < 0) return;
  if (!rowErrors[rowIndex]) rowErrors[rowIndex] = {};
  assignField(rowErrors[rowIndex], field, value);
}

function absorbIndexMap(rowErrors, fieldErrors, key, fields) {
  const match = INDEX_KEY.exec(key);
  if (!match || !fields || typeof fields !== 'object' || Array.isArray(fields)) return;
  const rowIndex = Number(match[1]);
  Object.keys(fields).forEach((field) => {
    assignRow(rowErrors, rowIndex, field, fields[field]);
    if (rowIndex === 0) assignField(fieldErrors, field, fields[field]);
  });
}

function absorbJoi(fieldErrors, rowErrors, item) {
  const parts = String(item.field).split('.').filter(Boolean);
  let index = 0;
  if (parts[index] === 'data') index += 1;

  let rowIndex = null;
  if (/^\d+$/.test(parts[index] || '')) {
    rowIndex = Number(parts[index]);
    index += 1;
  }

  const field = parts[index];
  if (!field || field === 'data' || /^\d+$/.test(field)) return;
  if (rowIndex != null) {
    assignRow(rowErrors, rowIndex, field, item.message);
    return;
  }
  assignField(fieldErrors, field, item.message);
}

function absorbNode(fieldErrors, rowErrors, node) {
  if (!node) return;
  if (Array.isArray(node)) {
    node.forEach((item) => absorbNode(fieldErrors, rowErrors, item));
    return;
  }
  if (typeof node !== 'object') return;
  if (typeof node.field === 'string') {
    absorbJoi(fieldErrors, rowErrors, node);
    return;
  }
  if (node.fieldErrors) absorbNode(fieldErrors, rowErrors, node.fieldErrors);
  Object.keys(node).forEach((key) => {
    if (INDEX_KEY.test(key)) absorbIndexMap(rowErrors, fieldErrors, key, node[key]);
  });
}

function withLocationAlias(fieldErrors) {
  if (fieldErrors.location && !fieldErrors.locationId) {
    fieldErrors.locationId = fieldErrors.location;
  }
  return Object.keys(fieldErrors).length ? fieldErrors : null;
}

/** Split a V2 error `data` payload into form fields and bulk row cells. */
export function chartOfAccountErrorParts(data) {
  const fieldErrors = {};
  const rowErrors = {};
  absorbNode(fieldErrors, rowErrors, data);
  Object.keys(rowErrors).forEach((key) => {
    const row = rowErrors[key];
    if (row.locationId && !row.location) row.location = row.locationId;
  });
  return {
    fieldErrors: withLocationAlias(fieldErrors),
    rowErrors: Object.keys(rowErrors).length ? rowErrors : null,
  };
}

/** Add/edit body. Only the keys the chart-of-account write accepts. */
export function chartOfAccountWriteBody(account = {}) {
  const source = account && typeof account === 'object' ? account : {};
  const body = {};
  WRITE_KEYS.forEach((key) => {
    if (source[key] !== undefined) body[key] = source[key];
  });
  return body;
}

function checklistRefs(value) {
  if (Array.isArray(value)) {
    return value.map((ref) => String(ref).trim()).filter(Boolean);
  }
  if (value == null || value === '') return [];
  return String(value).split(',').map((ref) => ref.trim()).filter(Boolean);
}

/** One bulk-upload row. Drops preview-only keys. */
export function chartOfAccountBulkRow(row = {}) {
  const source = row && typeof row === 'object' ? row : {};
  const body = {};
  BULK_KEYS.forEach((key) => {
    if (source[key] !== undefined) body[key] = source[key];
  });
  body.checklistref = checklistRefs(source.checklistref);
  return body;
}

export function chartOfAccountBulkRows(rows) {
  return (Array.isArray(rows) ? rows : []).map(chartOfAccountBulkRow);
}
